/**
 * Fuel_04 PDP Sticky Buy Bar
 * OFF by default — native sticky bar behavior remains unchanged.
 *
 * Intelligems variant — add ONE class to show it:
 *   document.documentElement.classList.add('ig-sticky-buybar-new');
 *   // also accepted on <body>
 *
 * Optional flag (same effect):
 *   window.STICKY_BUYBAR_NEW = true;
 *
 * Issue 1: tier/benefit badges sync only from quantity-breaks — no
 * hardcoded "Single" / "+ Free Gifts" defaults.
 * Issue 3: after-scroll sticky shows only when inline ATC is off screen
 * (scroll + visualViewport), desktop + mobile. Shows on load when ATC is
 * below the fold; hides whenever a meaningful slice of ATC is in view.
 */
(function () {
  'use strict';

  var CLASS_NAME = 'ig-sticky-buybar-new';
  var VARIANT_LABEL = 'ADD TO CART';
  var started = false;
  var lastEnabled = null;
  var syncing = false;
  var observers = [];
  var listeners = [];
  var syncTimers = [];
  var lastTier = '';
  var lastBenefit = '';
  var qbObserver = null;
  var flagObserver = null;
  var inlineAtcVisible = true;
  var observedAtc = null;
  var inlineAtcWatchBound = false;
  var MIN_INLINE_ATC_VISIBLE_PX = 40;

  function isEnabled() {
    if (window.STICKY_BUYBAR_NEW === false) return false;
    if (window.STICKY_BUYBAR_NEW === true) return true;
    return (
      document.documentElement.classList.contains(CLASS_NAME) ||
      (document.body && document.body.classList.contains(CLASS_NAME))
    );
  }

  function getBar() {
    return document.querySelector('[data-sticky-buybar], .sticky-atc');
  }

  function getInlineAtc() {
    return (
      document.querySelector('.atc-button') ||
      document.querySelector('product-form .product-form__submit') ||
      document.querySelector('.product-form__submit')
    );
  }

  function ensureClass() {
    if (!document.documentElement.classList.contains(CLASS_NAME)) {
      document.documentElement.classList.add(CLASS_NAME);
    }
    if (document.body && !document.body.classList.contains(CLASS_NAME)) {
      document.body.classList.add(CLASS_NAME);
    }
  }

  function on(el, type, fn, opts) {
    if (!el) return;
    el.addEventListener(type, fn, opts);
    listeners.push({ el: el, type: type, fn: fn, opts: opts });
  }

  function teardownInlineAtcWatch() {
    observedAtc = null;
    inlineAtcWatchBound = false;
  }

  function teardownListeners() {
    listeners.forEach(function (l) {
      l.el.removeEventListener(l.type, l.fn, l.opts);
    });
    listeners = [];
    observers.forEach(function (mo) {
      try {
        mo.disconnect();
      } catch (e) {}
    });
    observers = [];
    if (qbObserver) {
      try {
        qbObserver.disconnect();
      } catch (e) {}
      qbObserver = null;
    }
    teardownInlineAtcWatch();
    syncTimers.forEach(function (id) {
      clearTimeout(id);
    });
    syncTimers = [];
  }

  function getViewportBounds() {
    var vv = window.visualViewport;
    if (vv) {
      return { top: vv.offsetTop, bottom: vv.offsetTop + vv.height };
    }
    return { top: 0, bottom: window.innerHeight };
  }

  function clearBodyPad() {
    document.documentElement.style.removeProperty('--sticky-buybar-pad');
    if (document.body) document.body.style.removeProperty('padding-bottom');
  }

  function setBarVisible(bar, visible) {
    if (!bar) return;
    if (visible) {
      bar.classList.add('sticky-atc--active');
      bar.setAttribute('aria-hidden', 'false');
    } else {
      bar.classList.remove('sticky-atc--active');
      bar.setAttribute('aria-hidden', 'true');
    }
  }

  function updateBarVisibility(bar) {
    if (!bar || !isEnabled()) return;
    // Always-on sticky (not after_scroll): keep visible while variant enabled
    if (!bar.classList.contains('sticky-atc--after-scroll')) {
      setBarVisible(bar, true);
      return;
    }
    // after_scroll: show only when inline ATC is off screen (never together)
    setBarVisible(bar, !inlineAtcVisible);
  }

  function isInlineAtcOnScreen(atc) {
    if (!atc) return false;
    var rect = atc.getBoundingClientRect();
    var vp = getViewportBounds();
    var visibleTop = Math.max(rect.top, vp.top);
    var visibleBottom = Math.min(rect.bottom, vp.bottom);
    // Require a meaningful slice visible — avoids mobile viewport/IO flicker while scrolling.
    return visibleBottom - visibleTop >= MIN_INLINE_ATC_VISIBLE_PX;
  }

  function syncInlineAtcVisibility() {
    var atc = getInlineAtc();
    inlineAtcVisible = atc ? isInlineAtcOnScreen(atc) : false;
    updateBarVisibility(getBar());
  }

  function watchInlineAtc() {
    var atc = getInlineAtc();
    if (!atc) {
      // No inline ATC — sticky is the only CTA; show it
      inlineAtcVisible = false;
      updateBarVisibility(getBar());
      observedAtc = null;
      return;
    }

    if (observedAtc === atc && inlineAtcWatchBound) {
      syncInlineAtcVisibility();
      return;
    }

    observedAtc = atc;

    if (!inlineAtcWatchBound) {
      var onViewportChange = function () {
        syncInlineAtcVisibility();
      };

      on(window, 'scroll', onViewportChange, { passive: true });
      on(window, 'resize', onViewportChange, { passive: true });
      if (window.visualViewport) {
        on(window.visualViewport, 'scroll', onViewportChange, { passive: true });
        on(window.visualViewport, 'resize', onViewportChange, { passive: true });
      }
      inlineAtcWatchBound = true;
    }

    syncInlineAtcVisibility();
  }

  function getSelectedBreak() {
    var root = document.querySelector('quantity-breaks');
    if (!root) return null;

    // Prefer adjacent label for the checked radio (input + label markup)
    var adjacent = root.querySelector(
      'input[type="radio"][name="quantity"]:checked + label.quantity-break, input[type="radio"][name="quantity"]:checked + .quantity-break'
    );
    if (adjacent) return adjacent;

    var checked =
      root.querySelector('input[type="radio"][name="quantity"]:checked') ||
      root.querySelector('input[type="radio"]:checked');
    if (!checked) return null;

    if (
      checked.nextElementSibling &&
      checked.nextElementSibling.classList.contains('quantity-break')
    ) {
      return checked.nextElementSibling;
    }

    if (checked.id) {
      var byFor = root.querySelector('label.quantity-break[for="' + checked.id + '"]');
      if (byFor) return byFor;
    }

    var dataInput = checked.getAttribute('data-input');
    if (dataInput) {
      return root.querySelector('label.quantity-break[data-input="' + dataInput + '"]');
    }

    return null;
  }

  function formatTierLabel(raw) {
    var t = (raw || '').replace(/\s+/g, ' ').trim();
    if (!t) return '';
    var lower = t.toLowerCase();
    if (lower === 'friends pack' || lower === 'friends') return 'Friends Pack';
    if (lower === 'family pack' || lower === 'family') return 'Family Pack';
    if (lower === 'single') return 'Single';
    return t;
  }

  function showBadgeEl(el) {
    if (!el) return;
    el.removeAttribute('hidden');
    el.style.removeProperty('display');
    el.style.removeProperty('visibility');
    el.style.removeProperty('opacity');
  }

  function hideBadgeEl(el) {
    if (!el) return;
    el.setAttribute('hidden', '');
  }

  function syncTitle(bar) {
    var titleEl = bar.querySelector('.sticky-atc__title');
    if (!titleEl) return;

    // Prefer the visible h1 only — `.product__title` wraps both an h1 and a
    // nested <a class="product__title"> with the same text, so reading the
    // container doubles the title.
    var productTitle =
      document.querySelector('.product__title > h1') ||
      document.querySelector('h1.product__title') ||
      document.querySelector('[data-product-title]');
    if (productTitle) {
      var text = productTitle.textContent.replace(/\s+/g, ' ').trim();
      if (text) titleEl.textContent = text;
    }
  }

  function ensureBadges(bar) {
    var heading = bar.querySelector('.sticky-atc__heading');
    if (!heading) {
      var left = bar.querySelector('.sticky-atc__left');
      if (!left) return null;
      heading = document.createElement('div');
      heading.className = 'sticky-atc__heading';
      left.insertBefore(heading, left.firstChild);
    }

    var badges = heading.querySelector('[data-sticky-badges]');
    if (!badges) {
      badges = document.createElement('div');
      badges.className = 'sticky-atc__badges';
      badges.setAttribute('data-sticky-badges', '');
      badges.setAttribute('hidden', '');
      badges.innerHTML =
        '<span class="sticky-atc__tier" data-sticky-tier hidden></span>' +
        '<span class="sticky-atc__benefit" data-sticky-benefit hidden></span>';
      heading.appendChild(badges);
    }

    var tierEl = badges.querySelector('[data-sticky-tier]');
    var benefitEl = badges.querySelector('[data-sticky-benefit]');
    if (!tierEl) {
      tierEl = document.createElement('span');
      tierEl.className = 'sticky-atc__tier';
      tierEl.setAttribute('data-sticky-tier', '');
      tierEl.setAttribute('hidden', '');
      badges.insertBefore(tierEl, badges.firstChild);
    }
    if (!benefitEl) {
      benefitEl = document.createElement('span');
      benefitEl.className = 'sticky-atc__benefit';
      benefitEl.setAttribute('data-sticky-benefit', '');
      benefitEl.setAttribute('hidden', '');
      badges.appendChild(benefitEl);
    }

    return { badges: badges, tierEl: tierEl, benefitEl: benefitEl };
  }

  function syncBadges(bar) {
    var els = ensureBadges(bar);
    if (!els) return;

    var tier = '';
    var benefit = '';
    // Gift cards never get tier/benefit badges. Otherwise only mirror
    // live quantity-breaks — no Liquid leftovers, no invented "+ Free Gifts".
    var allowBadges =
      !bar.hasAttribute('data-product-gift-card') &&
      !!document.querySelector('quantity-breaks');
    var qb = allowBadges ? getSelectedBreak() : null;

    if (qb) {
      var labelText = qb.querySelector('.quantity-break__label-text');
      var benefitText = qb.querySelector('.quantity-break__benefit');
      tier = labelText ? labelText.textContent.replace(/\s+/g, ' ').trim() : '';
      // Benefit badge only when this option actually declares one
      // (e.g. "+ Free Gifts") — blank options stay hidden.
      benefit = benefitText
        ? benefitText.textContent.replace(/\s+/g, ' ').trim()
        : '';
    }

    tier = formatTierLabel(tier);
    lastTier = tier;
    lastBenefit = benefit;

    if (tier) {
      els.tierEl.textContent = tier;
      showBadgeEl(els.tierEl);
    } else {
      els.tierEl.textContent = '';
      hideBadgeEl(els.tierEl);
    }

    if (benefit) {
      els.benefitEl.textContent = benefit;
      showBadgeEl(els.benefitEl);
    } else {
      els.benefitEl.textContent = '';
      hideBadgeEl(els.benefitEl);
    }

    if (tier || benefit) {
      showBadgeEl(els.badges);
    } else {
      hideBadgeEl(els.badges);
    }
  }

  function syncSelection() {
    var bar = getBar();
    if (!bar || !isEnabled()) return;
    syncBadges(bar);
    syncBundlePrice(bar);
  }

  function syncBundlePrice(bar) {
    var bundlePrice = bar.querySelector('[data-sticky-bundle-price]');
    var nativePrice = bar.querySelector('[data-sticky-price]');
    if (!bundlePrice) {
      // No bundle slot — keep native price visible
      if (nativePrice) {
        nativePrice.style.setProperty('display', 'block', 'important');
      }
      return;
    }

    var sale = '';
    var compare = '';

    var qb = getSelectedBreak();
    if (qb) {
      var priceEl = qb.querySelector('.quantity-break__price');
      var compareEl = qb.querySelector('.quantity-break__compare-price');
      if (priceEl) sale = priceEl.textContent.replace(/\s+/g, ' ').trim();
      if (compareEl) compare = compareEl.textContent.replace(/\s+/g, ' ').trim();
    }

    // Fallback to Liquid-rendered price snippet inside the sticky bar
    if (!sale && nativePrice) {
      var saleItem =
        nativePrice.querySelector('.price-item--sale') ||
        nativePrice.querySelector('.price__sale .price-item--last') ||
        nativePrice.querySelector('.price-item--last');
      var regularItem =
        nativePrice.querySelector('.price__sale .price-item--regular') ||
        nativePrice.querySelector('.price-item--regular');
      if (saleItem) {
        sale = saleItem.textContent.replace(/\s+/g, ' ').trim();
      } else {
        sale = nativePrice.textContent.replace(/\s+/g, ' ').trim();
      }
      if (regularItem) {
        compare = regularItem.textContent.replace(/\s+/g, ' ').trim();
      }
      // If snippet marks on-sale but we couldn't parse compare, keep sale text only
      if (
        !compare &&
        nativePrice.querySelector('.price--on-sale, .price__badge-sale, .badge')
      ) {
        // leave compare blank; still show sale price
      }
    }

    if (!sale) {
      hideBadgeEl(bundlePrice);
      bundlePrice.innerHTML = '';
      bundlePrice.setAttribute('aria-hidden', 'true');
      if (nativePrice) {
        nativePrice.style.setProperty('display', 'block', 'important');
      }
      return;
    }

    if (nativePrice) {
      nativePrice.style.setProperty('display', 'none', 'important');
    }

    // Don't show identical compare/sale as a strikethrough pair
    if (compare && compare === sale) compare = '';

    var html =
      '<span class="sticky-atc__prices">' +
      '<span class="sticky-atc__sale-price">' +
      sale +
      '</span>';
    if (compare) {
      html += ' <s class="sticky-atc__compare-price">' + compare + '</s>';
    }
    html += '</span>';

    // SALE badge only for real product compare_at sales — not bundle
    // quantity-break "was" prices when compare_at is blank.
    var tpl = bar.querySelector('[data-sticky-sale-badge-template]');
    var onSale =
      bar.hasAttribute('data-product-on-sale') ||
      !!(nativePrice && nativePrice.querySelector('.price--on-sale'));
    if (tpl && onSale) {
      html += tpl.innerHTML;
    }

    bundlePrice.innerHTML = html;
    showBadgeEl(bundlePrice);
    bundlePrice.setAttribute('aria-hidden', 'false');
  }

  function syncButtonLabel(bar, enabled) {
    var btn = bar.querySelector('[data-sticky-atc-btn]');
    var label = bar.querySelector('[data-sticky-atc-label]');
    if (!btn || !label) return;
    if (btn.disabled) return;

    if (enabled) {
      label.textContent = VARIANT_LABEL;
    } else {
      var def = btn.getAttribute('data-sticky-default-label');
      if (def) label.textContent = def;
    }
  }

  function syncAll() {
    var bar = getBar();
    if (!bar || !isEnabled()) return;
    clearBodyPad();
    watchInlineAtc();
    updateBarVisibility(bar);
    syncTitle(bar);
    syncBadges(bar);
    syncBundlePrice(bar);
    syncButtonLabel(bar, true);
    // Re-run price sync after layout so quantity-breaks / variant price are ready
    syncBundlePrice(bar);
    watchQuantityBreaks();
  }

  function scheduleSync() {
    syncAll();
    syncTimers.forEach(function (id) {
      clearTimeout(id);
    });
    syncTimers = [0, 50, 150, 400, 800].map(function (ms) {
      return setTimeout(syncAll, ms);
    });
  }

  function isQuantityBreakEvent(t) {
    if (!t || !t.closest) return false;
    return !!(
      t.closest('quantity-breaks') ||
      (t.matches &&
        (t.matches('quantity-breaks input[type="radio"]') ||
          t.matches('input[name="quantity"]') ||
          t.matches('label.quantity-break')))
    );
  }

  function watchQuantityBreaks() {
    var qb = document.querySelector('quantity-breaks');
    if (!qb || !window.MutationObserver) return;

    // Re-bind when linked-options replaces the quantity-breaks node
    if (qbObserver && qbObserver._target === qb) return;

    if (qbObserver) {
      try {
        qbObserver.disconnect();
      } catch (e) {}
    }

    qbObserver = new MutationObserver(function () {
      scheduleSync();
    });
    qbObserver._target = qb;
    qbObserver.observe(qb, {
      subtree: true,
      childList: true,
      characterData: true,
      attributes: true,
    });
  }

  function startFeature() {
    if (started) {
      scheduleSync();
      return;
    }
    started = true;
    ensureClass();

    var bar = getBar();
    if (!bar) return;

    scheduleSync();

    // Capture-phase so we still sync if anything stops bubbling
    on(
      document,
      'change',
      function (e) {
        var t = e.target;
        if (!t) return;
        if (
          (t.matches && t.matches('input[type="radio"][name="quantity"]')) ||
          isQuantityBreakEvent(t)
        ) {
          syncSelection();
          scheduleSync();
        }
      },
      true
    );

    on(
      document,
      'click',
      function (e) {
        var t = e.target;
        if (!t || !t.closest) return;
        if (
          t.closest('label.quantity-break') ||
          t.closest('quantity-breaks') ||
          t.closest('[data-linked-option]') ||
          t.closest('.linked-options') ||
          t.closest('variant-radios') ||
          t.closest('variant-selects')
        ) {
          // Radio may not be checked yet during click — sync after paint
          syncSelection();
          requestAnimationFrame(function () {
            syncSelection();
            scheduleSync();
          });
        }
      },
      true
    );

    on(window, 'resize', function () {
      watchInlineAtc();
    });

    on(document, 'shopify:section:load', scheduleSync);
    on(document, 'shopify:section:reorder', scheduleSync);
    on(window, 'pageshow', scheduleSync);

    // Linked options / animal picker often rebuilds product markup
    var main = document.getElementById('MainContent') || document.body;
    if (main && window.MutationObserver) {
      var mo = new MutationObserver(function (mutations) {
        for (var i = 0; i < mutations.length; i++) {
          var m = mutations[i];
          if (m.type !== 'childList') continue;
          var nodes = [];
          if (m.addedNodes && m.addedNodes.length) {
            for (var j = 0; j < m.addedNodes.length; j++) nodes.push(m.addedNodes[j]);
          }
          if (m.removedNodes && m.removedNodes.length) {
            for (var k = 0; k < m.removedNodes.length; k++) nodes.push(m.removedNodes[k]);
          }
          for (var n = 0; n < nodes.length; n++) {
            var node = nodes[n];
            if (!node || node.nodeType !== 1) continue;
            if (
              node.matches &&
              (node.matches(
                'quantity-breaks, .sticky-atc, [data-sticky-buybar], .product, product-info, .atc-button, .product-form__submit'
              ) ||
                (node.querySelector &&
                  node.querySelector(
                    'quantity-breaks, .sticky-atc, [data-sticky-buybar], .quantity-break, .atc-button, .product-form__submit'
                  )))
            ) {
              scheduleSync();
              return;
            }
          }
        }
      });
      mo.observe(main, { childList: true, subtree: true });
      observers.push(mo);
    }

    watchQuantityBreaks();
    watchInlineAtc();
  }

  function stopFeature() {
    teardownListeners();
    started = false;
    inlineAtcVisible = true;
    // Do not strip Intelligems class here — only pause our behavior.
    // Class removal is owned by Intelligems / explicit disable().

    var bar = getBar();
    if (bar) {
      bar.classList.remove('sticky-atc--active');
      bar.removeAttribute('aria-hidden');
      syncButtonLabel(bar, false);
      var bundlePrice = bar.querySelector('[data-sticky-bundle-price]');
      if (bundlePrice) {
        bundlePrice.innerHTML = '';
        bundlePrice.setAttribute('hidden', '');
        bundlePrice.setAttribute('aria-hidden', 'true');
      }
    }
    clearBodyPad();
    document.dispatchEvent(new Event('scroll'));
  }

  function syncVariant() {
    if (syncing) return;
    var enabled = isEnabled();

    syncing = true;
    try {
      if (!enabled) {
        if (started) {
          stopFeature();
        }
        lastEnabled = false;
        return;
      }

      lastEnabled = true;
      ensureClass();
      if (!started) {
        startFeature();
      }
    } finally {
      syncing = false;
    }
  }

  function watchFlags() {
    if (!window.MutationObserver || flagObserver) return;
    flagObserver = new MutationObserver(function () {
      syncVariant();
    });
    flagObserver.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class'],
    });
    if (document.body) {
      flagObserver.observe(document.body, {
        attributes: true,
        attributeFilter: ['class'],
      });
    }
  }

  setInterval(function () {
    syncVariant();
  }, 500);

  function boot() {
    syncVariant();
    watchFlags();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  window.__stickyBuybarV1 = {
    sync: scheduleSync,
    enable: function () {
      document.documentElement.classList.add(CLASS_NAME);
      if (document.body) document.body.classList.add(CLASS_NAME);
      window.STICKY_BUYBAR_NEW = true;
      syncVariant();
    },
    disable: function () {
      document.documentElement.classList.remove(CLASS_NAME);
      if (document.body) document.body.classList.remove(CLASS_NAME);
      window.STICKY_BUYBAR_NEW = false;
      lastEnabled = null;
      stopFeature();
    },
  };
})();
